/**
 * Example-value hints for the field pickers (Filter / Group / Sort / Columns):
 * the muted second line under a field's name. Pure leaf — no imports — so the
 * catalogs (`filter-fields.ts`, `signal-view.ts`) can apply it without a cycle.
 *
 * Why tables here instead of a `hint:` on every catalog entry: People and
 * Companies catalogs are GENERATED from `CONTACT_FIELDS` / `ACCOUNT_FIELDS`
 * (`fromColumnFields`), and the Signals catalog re-generates its Contact /
 * Account half from those again — one keyed table per entity is applied at the
 * end of each composition (`withHints`) so a hint is written once and every tab
 * that offers the field shows it.
 *
 * Rules (enforced by `__tests__/field-hints.test.ts`):
 *  - every field's line is ONLY examples, "e.g. X, Y" — never a description;
 *  - a field with a closed `values` list needs NO entry — `fieldExamples`
 *    derives the line from the list, so it cannot drift;
 *  - booleans, dates and people take their type's line (`TYPE_EXAMPLES`);
 *  - each line is <= `FIELD_EXAMPLES_MAX_CHARS` (40) so it stays on one row;
 *  - picklist examples are real values (checked against prod 2026-10-21; the
 *    lines in these tables are hand-maintained, not derived from SELECT DISTINCT); names,
 *    emails, domains and ids are invented placeholders (Dana Lee,
 *    dana@example.com, example.com, 001A0000…) — never a real customer.
 *  - only fields with NO data anywhere sit on `UNKNOWN_VALUE_FIELD_KEYS`.
 *
 * Wording has NOT been through /uxw.
 */

/**
 * People tab (contact catalog) keys. Booleans, dates and people carry no entry
 * (`TYPE_EXAMPLES`); a closed `values` list needs none either. Account
 * cross-join keys are `account_<key>`.
 */
export const CONTACT_FIELD_HINTS: Readonly<Record<string, string>> = {
  name: 'e.g. Dana Lee',
  title: 'e.g. VP of Marketing',
  email: 'e.g. dana@example.com',
  best_phone: 'e.g. +1 555 0100',
  fit_score: 'e.g. 72',
  icp_fit_score: 'e.g. 85',
  persona: 'e.g. Sales, Executive, Marketing',
  persona_tier: 'e.g. INF, DM, DQ',
  job_level: 'e.g. Executive, Mid-Senior, Manager',
  contact_stage: 'e.g. Unaware, Engaged, Dormant User',
  total_engagement_score: 'e.g. 42',
  product_engagement_score: 'e.g. 18',
  signals_engagement_score: 'e.g. 24',
  gong_active_flows: 'e.g. 1',
  gong_flow_name: 'e.g. New-lead follow-up',
  suppression_reasons: 'e.g. Opted Out, Generic Email',
  lim_combined: 'e.g. Meeting Booked',
  lim_type: 'e.g. Product Event, Hand Raising Event',
  lim_subtype: 'e.g. Workspace Sign-up, Meeting Booked',
  lim_name: 'e.g. Demo Request - Site demo form',
  lim_description: 'e.g. Inbound Form - Site demo form',
  mailing_city: 'e.g. San Francisco',
  mailing_state: 'e.g. CA, NY',
  mailing_country: 'e.g. United States, India',
  previous_job_change_type: 'e.g. Product User, Job Change',
  past_employers_clay_customers: 'e.g. Example Co',
  came_from: 'e.g. Example Co',
  new_employer: 'e.g. Example Co',
  new_title: 'e.g. Head of Growth',
  new_contact: 'e.g. Dana Lee',
  play_status: 'e.g. Attended, No-show, Registered',
  play_invitee_type: 'e.g. Enterprise Customer, General Invite',
  play_last_queue_sent_by: 'e.g. Dana Lee',
  signal_subtype: 'e.g. Fall webinar, Hybrid Outbound',
  signal_name: 'e.g. Campaign · Event · Fall webinar',
  signal_invitee_type: 'e.g. Enterprise Customer, General Invite',
  signal_status_rank: 'e.g. 2',
  tracker_note: 'e.g. Follow up after demo',
  tracker_rank: 'e.g. 3',
  account_f500_rank: 'e.g. 212',
};

/** Companies tab (account catalog) keys. */
export const ACCOUNT_FIELD_HINTS: Readonly<Record<string, string>> = {
  name: 'e.g. Example Co',
  email_domain_link: 'e.g. example.com',
  propensity_score: 'e.g. 72',
  fit_score: 'e.g. 85',
  lim_type: 'e.g. Product Event, Hand Raising Event',
  lim_subtype: 'e.g. Meeting Booked, Workspace Sign-up',
  lim_description: 'e.g. Inbound Form - Site demo form',
  lim_name: 'e.g. Demo Request - Site demo form',
  l90_signal_count: 'e.g. 6',
  l90_website_visits: 'e.g. 14',
  l90_active_clay_users: 'e.g. 3',
  workspaces_created_last_90_days: 'e.g. 2',
  number_of_workspace: 'e.g. 4',
  number_of_employees: 'e.g. 250',
  number_of_open_opp: 'e.g. 3',
  engagement_score_90_days: 'e.g. 38',
  engagement_status_90_days: 'e.g. No Signal, Lightly Engaged',
  arr: 'e.g. 48,000',
  arr_opportunity: 'e.g. 9,600',
  pcv: 'e.g. 120,000',
  gs_arr: 'e.g. 96,000',
  open_pipeline: 'e.g. 60,000',
  credit_pace: 'e.g. 0.02, 50.46',
  credits_l30: 'e.g. 4,200',
  fortune_500_rank: 'e.g. 212',
  company_segment: 'e.g. Early Stage Startup',
  account_type: 'e.g. Prospect, Customer',
  last_opportunity_stage: 'e.g. Disqualified, Closed Lost',
  region: 'e.g. NAMER, EMEA, APAC, LATAM',
  billing_city: 'e.g. London, New York',
  billing_country: 'e.g. United States, India',
  industry: 'e.g. Computer Software',
  sales_tier: 'e.g. Tier 1, Tier 2, Tier 3',
  account_fit: 'e.g. Best Fit, Good Fit, Moderate Fit',
  product_tier: 'e.g. Launch, Growth, Pro',
  plan_interval: 'e.g. Monthly, Annual, Custom',
  red_reason: 'e.g. Pacing below floor',
  next_gs: 'e.g. Dana Lee',
  parent_account_id: 'e.g. 001A0000…',
  ultimate_global_parent_id: 'e.g. 001A0000…',
  tech_stack: 'e.g. Salesforce, HubSpot, Slack',
  technologies_used: 'e.g. Salesforce, HubSpot, Snowflake',
  tracker_note: 'e.g. Follow up after demo',
  tracker_rank: 'e.g. 3',
};

/** Signals tab keys (hand-written + `terra_signals` fields). Generated Contact/Account passthrough fields inherit the two tables above. */
export const SIGNAL_FIELD_HINTS: Readonly<Record<string, string>> = {
  contact_id: 'e.g. 003A0000…',
  account_id: 'e.g. 001A0000…',
  contact_linkedin: 'e.g. example.com/in/dana-lee',
  account_linkedin: 'e.g. example.com/company/example-co',
  signal_id: 'e.g. 48213',
  signal_instance_id: 'e.g. 701A0000…, generate_message',
  signal_status_rank: 'e.g. 2',
  signal_subtype: 'e.g. Fall webinar, Hybrid Outbound',
  signal_name: 'e.g. Campaign · Event · Fall webinar',
  signal_source: 'e.g. Campaign Member, Events and Signals',
  signal_source_type: 'e.g. Event, Hybrid Outbound, News',
  signal_source_subtype: 'e.g. GTM Play, Clay Hosted, High Intent',
  signal_tactic: 'e.g. Call, Manual email, Auto email',
  step_number: 'e.g. 3',
  flow_name: 'e.g. New-lead follow-up',
  campaign_name: 'e.g. Fall webinar',
  invitee_type: 'e.g. Enterprise Customer, General Invite',
  play_type: 'e.g. Workspace Creation',
  days_open: 'e.g. 5',
  action_reason: 'e.g. Finished (no reply), To-do expired',
  action_completed_by: 'e.g. priya@example.com',
  action_completed_by_id: 'e.g. 005A0000…',
  assignee_role: 'e.g. Sales Development Representative',
  assignee_manager: 'e.g. Taylor Brooks',
  assignee_id: 'e.g. 005B0000…',
  assignee_email: 'e.g. dana@example.com',
  source_record_id: 'e.g. a42A0000…',
  source_link: 'e.g. example.com/record',
  event_date: 'e.g. Nov 12',
  action_completed_at: 'e.g. Oct 20',
  snooze_until: 'e.g. Oct 30',
  account_lim_date: 'e.g. Sep 18',
  account_last_gtme_meeting: 'e.g. Oct 8',
  account_last_gs_meeting: 'e.g. Oct 2',
  account_last_claydr_meeting: 'e.g. Sep 25',
  account_created_date: 'e.g. Jan 14',
  account_renewal_date: 'e.g. Dec 15',
  account_next_gs_assign_date: 'e.g. Nov 6',
  contact_last_workspace_login: 'e.g. Oct 16',
  contact_last_email_sent: 'e.g. Oct 14',
  contact_last_email_date: 'e.g. Oct 12',
  contact_last_inbound_form: 'e.g. Sep 30',
  contact_last_activity: 'e.g. Oct 19',
  contact_lim_date: 'e.g. Sep 9',
  contact_job_start_date: 'e.g. Mar 3',
  contact_created_date: 'e.g. Feb 21',
  contact_last_gtme_email: 'e.g. Oct 6',
  contact_last_gs_meeting: 'e.g. Sep 22',
  contact_last_claydr_meeting: 'e.g. Sep 15',
  contact_last_claydr_email: 'e.g. Oct 1',
  contact_last_gtme_meeting: 'e.g. Sep 29',
  contact_last_trial_start: 'e.g. Aug 27',
  flow_enrolled_at: 'e.g. Oct 9',
  flow_last_modified_at: 'e.g. Oct 17',
  gong_verified_at: 'e.g. Oct 22',
  step_date: 'e.g. Oct 21',
  action_completed_by_name: 'e.g. Priya Shah',
  account_owner_name: 'e.g. Alex Morgan',
  account_claydr_name: 'e.g. Elena Ruiz',
  account_gs_name: 'e.g. Sam Patel',
  account_next_gs: 'e.g. Jordan Kim',
  contact_name: 'e.g. Marcus Chen',
  contact_email: 'e.g. marcus@example.com',
  account_number_of_open_opp: 'e.g. 9',
  account_l90_active_clay_users: 'e.g. 12',
  account_workspaces_created_last_90_days: 'e.g. 7',
  account_name: 'e.g. Example Co',
  contact_past_employers_clay_customers: 'e.g. Acme Widgets',
  account_billing_country: 'e.g. United States, Germany',
  contact_mailing_country: 'e.g. India, Canada',
  account_propensity_score: 'e.g. 72',
  contact_fit_score: 'e.g. 64',
  account_fit_score: 'e.g. 85',
  contact_icp_fit_score: 'e.g. 91',
  contact_lim_name: 'e.g. Webinar Signup - Fall webinar',
  contact_lim_type: 'e.g. Inbound Form, Content Download',
  contact_lim_description: 'e.g. Event Registration - Fall webinar',
  contact_gong_flow_name: 'e.g. Welcome sequence',
};

/** Plays tab (`PLAYS_FILTER_FIELDS`) keys. */
export const PLAYS_FIELD_HINTS: Readonly<Record<string, string>> = {
  play_type: 'e.g. Event Invite, Warm Intro, Gong Flow',
  play_name: 'e.g. Fall webinar',
  status: 'e.g. Planned, Active, Completed',
  city: 'e.g. Austin',
  target_persona: 'e.g. RevOps, Marketing Ops, Sales',
  capacity: 'e.g. 50',
  invite_subject: 'e.g. Fall webinar invite',
  invite_body: 'e.g. Hi Dana, we would love to see you',
};

/**
 * Re-keys `hints` under `prefix` (and keeps keys that already carry it), for
 * catalogs that re-offer another entity's fields as `<prefix><key>` — the
 * contact catalog's `account_*` cross-join fields, the account catalog's
 * `contact_*` EXISTS fields, and the Signals catalog's Contact/Account half.
 */
export function prefixedHints(prefix: string, hints: Readonly<Record<string, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, hint] of Object.entries(hints)) {
    out[key.startsWith(prefix) ? key : `${prefix}${key}`] = hint;
  }
  return out;
}

/** Hints as the People catalog applies them: its own plus the Account fields it cross-joins. */
export const CONTACT_CATALOG_HINTS: Readonly<Record<string, string>> = { ...CONTACT_FIELD_HINTS, ...prefixedHints('account_', ACCOUNT_FIELD_HINTS) };
/** Hints as the Companies catalog applies them: its own plus the Contact fields it filters through EXISTS. */
export const ACCOUNT_CATALOG_HINTS: Readonly<Record<string, string>> = { ...ACCOUNT_FIELD_HINTS, ...prefixedHints('contact_', CONTACT_FIELD_HINTS) };
/** Hints as the Signals catalog applies them: its own plus the generated Contact / Account fields. */
export const SIGNAL_CATALOG_HINTS: Readonly<Record<string, string>> = {
  ...prefixedHints('contact_', CONTACT_FIELD_HINTS),
  ...prefixedHints('account_', ACCOUNT_FIELD_HINTS),
  ...SIGNAL_FIELD_HINTS,
};

/**
 * Examples every field of a given TYPE carries unless its key has its own entry
 * — these shapes are intrinsic to the type (a yes/no flag holds Yes or No, a
 * date holds a date, a person picker holds a person's name), not a guessed
 * default for config/DB data.
 */
export const TYPE_EXAMPLES: Readonly<Record<string, string>> = {
  boolean: 'e.g. Yes, No',
  date: 'e.g. Oct 24',
  person: 'e.g. Dana Lee',
};

/**
 * Stamps `hint` onto entries that have none yet: the keyed table's line, else
 * the type's (`TYPE_EXAMPLES`). Entries with a closed `values` list are left
 * alone — `fieldExamples` derives their line.
 */
export function withHints<T extends { key: string; type?: string; hint?: string; values?: readonly unknown[] }>(
  entries: readonly T[],
  hints: Readonly<Record<string, string>>,
): T[] {
  return entries.map(entry => {
    if (entry.hint !== undefined || (entry.values?.length ?? 0) > 0) return entry;
    const hint = hints[entry.key] ?? (entry.type !== undefined ? TYPE_EXAMPLES[entry.type] : undefined);
    return hint === undefined ? entry : { ...entry, hint };
  });
}

// ── Coverage: every catalog entry has examples, or is explicitly unknown ──

/**
 * Catalog keys whose values are genuinely unknown, each with the reason. A
 * field belongs here only after the real data was checked and held nothing.
 * Looked up by the key or the key minus its `contact_` / `account_` / `play_`
 * prefix (those catalogs re-offer another entity's field). Anything else with
 * no closed `values` list and no hint fails `field-hints.test.ts`.
 */
export const UNKNOWN_VALUE_FIELD_KEYS: Readonly<Record<string, string>> = {
  priority_stamp: 'prod-mirror: priority_stamp is NULL in every row (checked 2026-10-21: 31,959 of 31,959 mirror rows, 396,034 of 396,034 view rows), so no real value shape exists to show',
};

const ENTITY_PREFIX = /^(contact_|account_|play_)/;

/** Words that make a line a sentence rather than a list of example values. */
const DESCRIPTOR_WORDS = /\b(the|they|them|their|is|are|was|were|will|who|whose|why|what|when|which|that|this|since|from|with|has|have|under)\b/i;

/**
 * Why `hint` could expose real customer/contact data, or undefined if it is
 * clean. Only the `example.com` placeholder domain is allowed; no URL scheme,
 * no `www.`, no other domain, no 15/18-character Salesforce-id-shaped token.
 */
export function realDataInHint(hint: string): string | undefined {
  if (/https?:|www\./i.test(hint)) return 'contains a URL';
  for (const [, domain] of hint.matchAll(/@([\w.-]+)/g)) {
    if (domain.toLowerCase() !== 'example.com') return `email domain "${domain}" is not example.com`;
  }
  for (const [domain] of hint.matchAll(/\b[a-z0-9-]+\.(?:com|io|net|org|co|ai|dev|app)\b/gi)) {
    if (domain.toLowerCase() !== 'example.com') return `domain "${domain}" is not example.com`;
  }
  if (/\b(?=[A-Za-z]*\d)(?=\d*[A-Za-z])[0-9A-Za-z]{15,18}\b/.test(hint)) return 'contains a full-length record id';
  return undefined;
}

/**
 * Entries that break the picker-example rules. Empty = compliant. For each:
 *  - no non-empty `values` and no `hint` → needs examples, unless the key (or
 *    its un-prefixed form) is in `UNKNOWN_VALUE_FIELD_KEYS` / `extraAllowed`;
 *  - a `hint` must start "e.g. ", fit `maxChars`, read as example values (no
 *    sentence words) and carry no real-looking data.
 */
export function fieldHintGaps(
  entries: ReadonlyArray<{ key: string; hint?: string; values?: readonly unknown[] }>,
  maxChars: number,
  extraAllowed: Readonly<Record<string, string>> = {},
): string[] {
  const gaps: string[] = [];
  for (const entry of entries) {
    const { hint } = entry;
    if (hint !== undefined) {
      if (!hint.startsWith('e.g. ')) gaps.push(`${entry.key}: hint must start with "e.g. "`);
      else {
        if (hint.length > maxChars) gaps.push(`${entry.key}: hint is ${hint.length} chars (max ${maxChars})`);
        const word = DESCRIPTOR_WORDS.exec(hint)?.[1];
        if (word) gaps.push(`${entry.key}: hint reads like a description, not examples ("${word.toLowerCase()}")`);
        const real = realDataInHint(hint);
        if (real) gaps.push(`${entry.key}: hint may be real data (${real})`);
      }
    }
    if ((entry.values?.length ?? 0) > 0 || hint) continue;
    const base = entry.key.replace(ENTITY_PREFIX, '');
    if (entry.key in UNKNOWN_VALUE_FIELD_KEYS || base in UNKNOWN_VALUE_FIELD_KEYS || entry.key in extraAllowed) continue;
    gaps.push(`${entry.key}: no values and no hint (add examples, or allowlist with a reason if the values are unknown)`);
  }
  return gaps;
}
